import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';

import { cn } from '../../libraries/tailwind/utils';
import { Label } from '../ui/label';

const Form = FormProvider;

const FormFieldContext = React.createContext({});

const FormField = (
  {
    name,
    control,
    render,
    ...props
  }
) => {
  const contextValue = React.useMemo(() => ({ name }), [name]);

  return (
    (<FormFieldContext.Provider value={contextValue}>
      <Controller name={name} control={control} render={render} {...props} />
    </FormFieldContext.Provider>)
  );
};
const FormItemContext = React.createContext({});
const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState
  };
};



const FormItem = React.forwardRef(({ className, ...props }, ref) => {
  const id = React.useId();

  const contextValue = React.useMemo(() => ({ id }), [id]);

  return (
    (<FormItemContext.Provider value={contextValue}>
      <div ref={ref} className={cn('space-y-2', className)} {...props} />
    </FormItemContext.Provider>)
  );
});
FormItem.displayName = 'FormItem';

const FormLabel = React.forwardRef(({ className, required, ...props }, ref) => {
  const { formItemId } = useFormField();

  return (
    (<Label
      ref={ref}
      className={cn(className)}
      htmlFor={formItemId}
      required={required}
      {...props} />)
  );
});
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    (<Slot
      ref={ref}
      id={formItemId}
      className={cn(error && 'border-danger')}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props} />)
  );
});
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return (
    (<p
      ref={ref}
      id={formDescriptionId}
      className={cn('text-sm text-muted-foreground', className)}
      {...props} />)
  );
});
FormDescription.displayName = 'FormDescription';

const FormMessage = React.forwardRef(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message) : children;

  if (!body) {
    return null;
  }

  return (
    (<p
      ref={ref}
      id={formMessageId}
      className={cn('text-sm font-medium text-danger-foreground', className)}
      {...props}>
      {body}
    </p>)
  );
});
FormMessage.displayName = 'FormMessage';

FormField.propTypes = {
  name: PropTypes.string,
  control: PropTypes.any,
  render: PropTypes.func
};

FormItem.propTypes = {
  className: PropTypes.string
};

FormLabel.propTypes = {
  className: PropTypes.string,
  required: PropTypes.bool
};

FormControl.propTypes = {
  // Slot component props
};

FormDescription.propTypes = {
  className: PropTypes.string
};

FormMessage.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField
};
